
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuthContext } from '../../context/AuthContext';
import { useWS } from '../../hooks/useWS';
import { API } from '../../utils/api';
import { Layout } from '../../components/Layout';
import { MobPayHome } from './MobPayHome';
import { MobPayHistory } from './MobPayHistory';
import { MobPaySettings } from './MobPaySettings';
import { MobPayProfile } from './MobPayProfile';
import { Tools } from '../Tools';
import { PaidAnimation } from '../../components/PaidAnimation';

export function MobPayDashboard() {
  const { headers, user } = useAuthContext();
  const { status, messages } = useWS(user);
  const [scanningPayment, setScanningPayment] = useState(false);
  const [parents, setParents] = useState([]);
  const [selectedParent, setSelectedParent] = useState('');
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [externalToken, setExternalToken] = useState('');
  const [externalDeeplink, setExternalDeeplink] = useState('');
  const [staticPayee, setStaticPayee] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [analytics, setAnalytics] = useState({ today_spent: 0, month_spent: 0, month_limit: 10000, month_saved: 0, month_name: '' });
  
  // V3.1: PAID animation state
  const [showPaidAnimation, setShowPaidAnimation] = useState(false);
  const [paidAmount, setPaidAmount] = useState(null);
  const [processedPaidRequests, setProcessedPaidRequests] = useState(new Set());

  const fetchParents = async () => { 
    try {
      const res = await axios.get(`${API}/mobpay/parents`, { headers }); 
      setParents(res.data.items || []); 
    } catch (e) {
      if (e.response?.status === 403) {
        console.error('Auth error fetching parents - session may have expired');
      } else {
        toast.error('Failed to fetch payers');
      }
    }
  };
  
  const fetchMyRequests = async () => { 
    try {
      const res = await axios.get(`${API}/mobpay/requests`, { headers }); 
      setMyRequests(res.data.items || []); 
    } catch (e) {
      if (e.response?.status === 403) {
        console.error('Auth error fetching requests - session may have expired');
      } else {
        toast.error('Failed to fetch requests');
      }
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/mobpay/analytics`, { headers });
      if (res.data) {
        setAnalytics(res.data);
      }
    } catch (e) {
      console.error('Failed to fetch analytics', e);
    }
  };
  
  useEffect(() => { fetchParents(); fetchMyRequests(); fetchAnalytics(); }, []);
  useEffect(() => {
    const latest = messages[messages.length - 1]; if (!latest) return;
    if (latest.type === 'bound') { toast.success('Bound to payer'); fetchParents(); fetchAnalytics(); }
    if (latest.type === 'request_update') { 
      // V3.1: Show PAID animation (only once per request)
      if (latest.status === 'PAID' && !processedPaidRequests.has(latest.request_id)) {
        const request = myRequests.find(r => r.id === latest.request_id);
        setPaidAmount(request?.amount || latest.amount);
        setShowPaidAnimation(true);
        setProcessedPaidRequests(prev => new Set(prev).add(latest.request_id));
      } else if (latest.status !== 'PAID') {
        toast.message(`Request ${latest.request_id} ${latest.status}`);
      }
      fetchMyRequests(); 
      fetchAnalytics(); 
    }
  }, [messages, myRequests, processedPaidRequests]);

  const tokenLike = (s) => typeof s === 'string' && /^[A-Za-z0-9_-]{20,200}$/.test(s);
  
  // Parse Swish QR code formats:
  // Type A: A<swish_number> (e.g., A0708123456)
  // Type B: B<swish_number>;<amount> (e.g., B0708123456;100)
  // Type C: C<swish_number>;<amount>;<message>;<editable> (e.g., C0708123456;100;Order123;0)
  const parseSwishQR = (txt) => {
    const trimmed = txt.trim();
    
    // Type A: Just a Swish number with A prefix
    if (/^A\d{10,12}$/.test(trimmed)) {
      return { payee: trimmed.substring(1), amount: undefined, message: undefined };
    }
    
    // Type B: Swish number + amount
    const typeBMatch = trimmed.match(/^B(\d{10,12});(\d+(?:,\d{2})?)$/);
    if (typeBMatch) {
      const amount = typeBMatch[2].replace(',', '.');
      return { payee: typeBMatch[1], amount: parseFloat(amount), message: undefined };
    }
    
    // Type C: Swish number + amount + message + editable flags
    // Format: C<number>;<amount>;<message>;<editable>
    const typeCMatch = trimmed.match(/^C(\d{10,12});([^;]*);([^;]*);?(\d*)$/);
    if (typeCMatch) {
      const payee = typeCMatch[1];
      const amountStr = typeCMatch[2];
      const message = typeCMatch[3] ? decodeURIComponent(typeCMatch[3]) : undefined;
      const amount = amountStr ? parseFloat(amountStr.replace(',', '.')) : undefined;
      return { payee, amount, message };
    }
    
    // Also try without the editable field (simpler C format)
    const simpleCMatch = trimmed.match(/^C(\d{10,12})(?:;([^;]*))?(?:;([^;]*))?/);
    if (simpleCMatch) {
      const payee = simpleCMatch[1];
      const amountStr = simpleCMatch[2];
      const message = simpleCMatch[3] ? decodeURIComponent(simpleCMatch[3]) : undefined;
      const amount = amountStr ? parseFloat(amountStr.replace(',', '.')) : undefined;
      return { payee, amount, message };
    }
    
    // Fallback: Just a number (no prefix) - treat as payee
    if (/^\d{10,12}$/.test(trimmed)) {
      return { payee: trimmed, amount: undefined, message: undefined };
    }
    
    return null;
  };
  
  const parseStaticPayload = (txt) => {
    // First try Swish QR format (A/B/C types)
    const swishResult = parseSwishQR(txt);
    if (swishResult && swishResult.payee) {
      console.log('Parsed Swish QR:', swishResult);
      return swishResult;
    }
    
    // Try URL format (swish://payment?... or custom URL)
    try {
      const url = new URL(txt);
      const payee = url.searchParams.get('payee') || url.searchParams.get('phone');
      const am = url.searchParams.get('amount');
      const msg = url.searchParams.get('message') || url.searchParams.get('msg');
      if (payee || am || msg) return { payee, amount: am ? parseFloat(am) : undefined, message: msg };
    } catch {}
    
    // Try key-value lines format (PAYEE: xxx, AMOUNT: xxx)
    const lines = txt.split(/\r?\n/).map(s=>s.trim());
    if (lines.some(l => l.toUpperCase().startsWith('PAYEE'))) {
      const map = {};
      lines.forEach(l=>{ const [k,...rest]=l.split(':'); if(k&&rest.length){ map[k.trim().toUpperCase()] = rest.join(':').trim(); }});
      const payee = map['PAYEE'] || map['PHONE'];
      const am = map['AM'] || map['AMOUNT'];
      const msg = map['MSG'] || map['MESSAGE'];
      return { payee, amount: am ? parseFloat(am) : undefined, message: msg };
    }
    
    return null;
  };

  const onScanPayment = async (result) => {
    try {
      const txt = result?.getText?.() || '';
      // Full Swish deeplink
      if (txt.startsWith('swish://paymentrequest?')) { setExternalDeeplink(txt); setExternalToken(''); setStaticPayee(''); setScanningPayment(false); toast.info('Vendor deeplink captured.'); return; }
      // Token-like
      if (tokenLike(txt)) { setExternalToken(txt); setExternalDeeplink(''); setStaticPayee(''); setScanningPayment(false); toast.info('Vendor token captured.'); return; }
      // Swish QR or Static QR payload
      const parsed = parseStaticPayload(txt);
      if (parsed && (parsed.payee || parsed.amount || parsed.message)) {
        if (parsed.payee) setStaticPayee(parsed.payee);
        if (parsed.amount) setAmount(String(parsed.amount));
        if (parsed.message) setMessage(parsed.message);
        setExternalToken(''); setExternalDeeplink(''); setScanningPayment(false);
        
        // Show descriptive message
        const fields = [];
        if (parsed.payee) fields.push(`Swish: ${parsed.payee}`);
        if (parsed.amount) fields.push(`Amount: ${parsed.amount} SEK`);
        if (parsed.message) fields.push(`Message: ${parsed.message}`);
        toast.success(`✅ Swish QR scanned! ${fields.join(', ')}`);
        return;
      }
      toast.error('Unsupported QR code format. Expected Swish payment QR.');
    } catch (e) { setScanningPayment(false); toast.error('Scan failed'); }
  };

  const createRequest = async (extraData = {}) => {
    if (!selectedParent) { toast.error('Select a payer'); return; }
    const isExternal = externalToken || externalDeeplink;
    const isStatic = !!staticPayee;
    
    // Merge extraData (like category_name) into payload
    const basePayload = { 
      parent_id: selectedParent, 
      message,
      ...extraData 
    };

    const payload = isExternal
      ? { ...basePayload, external_token: externalToken || undefined, external_deeplink: externalDeeplink || undefined }
      : isStatic
        ? { ...basePayload, static_payee: staticPayee, amount: amount ? Number(amount) : undefined }
        : { ...basePayload, amount: Number(amount) };
        
    try {
      await axios.post(`${API}/requests/create`, payload, { headers });
      toast.success(isExternal ? 'Token/link sent to payer' : isStatic ? 'Static payment sent to payer' : 'Request sent');
      if (!isExternal) setAmount('');
      setMessage(''); setExternalToken(''); setExternalDeeplink(''); setStaticPayee('');
      fetchMyRequests();
      fetchAnalytics(); // Refresh analytics after creating request
    } catch (e) { toast.error(e.response?.data?.detail || 'Create request failed'); }
  };

  return (
    <>
      <Layout activeTab={activeTab} setActiveTab={setActiveTab} role="mobpay">
        {activeTab === 'home' && <MobPayHome parents={parents} selectedParent={selectedParent} setSelectedParent={setSelectedParent} amount={amount} setAmount={setAmount} message={message} setMessage={setMessage} staticPayee={staticPayee} setStaticPayee={setStaticPayee} externalToken={externalToken} externalDeeplink={externalDeeplink} createRequest={createRequest} scanningPayment={scanningPayment} setScanningPayment={setScanningPayment} onScanPayment={onScanPayment} headers={headers} analytics={analytics} myRequests={myRequests} />}
        {activeTab === 'history' && <MobPayHistory myRequests={myRequests} />}
        {activeTab === 'tools' && <Tools />}
        {activeTab === 'settings' && <MobPaySettings headers={headers} user={user} />}
        {activeTab === 'profile' && <MobPayProfile user={user} headers={headers} />}
      </Layout>
      
      {/* V3.1: PAID Animation Overlay */}
      {showPaidAnimation && (
        <PaidAnimation
          amount={paidAmount}
          onClose={() => {
            setShowPaidAnimation(false);
            setPaidAmount(null);
          }}
        />
      )}
    </>
  );
}
